// POS-02 back-office order from the Orders list. Compact layout approved by the owner (2026-10-21):
// Customer / Delivery address / Products sections on the left with icon fields, and a sunken right
// column with the summary, "Order from" chips (brand marks), delivery + payment, a payment-status
// switch and the buttons. The server prices the lines and quotes delivery (`POST …/orders/manual`),
// so the summary shows delivery as "from the address". Products come from the POS catalogue lookup.
import { useMemo, useState, type ReactNode, type SelectHTMLAttributes } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DISTRICTS, getDistrict, lineage, localLevelsOf, searchLocalLevels } from "@ady/geo";
import { t } from "@ady/i18n";
import {
  BrandIcon,
  Button,
  cx,
  Icon,
  MoneyInput,
  PhoneInput,
  phoneError,
  Popup,
  useToast,
  type BrandName,
  type IconName,
  useDebounced,
} from "@ady/ui";
import { errorMessage } from "../api/errors";
import { useIdempotencyKeys } from "../api/idempotency";
import { posApi, type PosItem } from "../pos/api";
import {
  commerceApi,
  commerceKeys,
  type ManualOrderRequest,
  type ManualPaymentMethod,
  type OrderSource,
} from "./api";
import { MethodMark, Money, money } from "./ui";

const SOURCES: { id: OrderSource; brand?: BrandName; icon?: IconName }[] = [
  { id: "phone", icon: "phone" },
  { id: "whatsapp", brand: "whatsapp" },
  { id: "instagram", brand: "instagram" },
  { id: "facebook", brand: "facebook" },
  { id: "tiktok", brand: "tiktok" },
  { id: "walk_in", icon: "store" },
  { id: "other", icon: "dots" },
];
const METHODS: ManualPaymentMethod[] = [
  "cod",
  "cash",
  "bank_transfer",
  "merchant_qr",
  "esewa",
  "khalti",
  "other",
];
type PayStatus = ManualOrderRequest["payment"]["status"];
type Service = ManualOrderRequest["service"];

export interface Line {
  item: PosItem;
  qty: number;
}

export function NewOrderDialog({
  open,
  onClose,
  orgId,
  storeId,
  onCreated,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  storeId: string;
  onCreated: (id: string) => void;
  /** Starts with this buyer filled in (Customers "…" → Create order; phone in E.164). */
  prefill?: { name: string; phone: string; source?: OrderSource };
}) {
  return (
    <Popup open={open} onClose={onClose} size="lg" label={t("commerce.orders.create.title")}>
      {open ? (
        <NewOrderForm
          orgId={orgId}
          storeId={storeId}
          onClose={onClose}
          onCreated={onCreated}
          prefill={prefill}
        />
      ) : null}
    </Popup>
  );
}

function NewOrderForm({
  orgId,
  storeId,
  onClose,
  onCreated,
  prefill,
}: {
  orgId: string;
  storeId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
  prefill?: { name: string; phone: string; source?: OrderSource };
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const keyFor = useIdempotencyKeys();
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  // A short or wrong number is warned about once the box is left (owner, 2026-10-21).
  const [phoneIssue, setPhoneIssue] = useState<string | null>(null);
  const [phoneLeft, setPhoneLeft] = useState(false);
  const [name, setName] = useState(prefill?.name ?? "");
  const [found, setFound] = useState<number | null | "none">(null);
  const [cityQuery, setCityQuery] = useState("");
  const [localLevel, setLocalLevel] = useState<string | null>(null);
  const [district, setDistrict] = useState<string | null>(null);
  const [tole, setTole] = useState("");
  const [landmark, setLandmark] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [picking, setPicking] = useState(false);
  const [source, setSource] = useState<OrderSource>(prefill?.source ?? "phone");
  const [service, setService] = useState<Service>("standard");
  const [method, setMethod] = useState<ManualPaymentMethod>("cod");
  const [payStatus, setPayStatus] = useState<PayStatus>("unpaid");
  const [paid, setPaid] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const findCustomer = useMutation({
    mutationFn: () => commerceApi.customers(orgId, storeId, phone.replace("+977", "")),
    onSuccess: (r) => {
      const hit = r.data[0];
      if (hit) {
        setName(hit.name);
        setFound(hit.orders_count);
      } else setFound("none");
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });

  const subtotal = lines.reduce((a, l) => a + l.item.price_paisa * l.qty, 0);
  const count = lines.reduce((a, l) => a + l.qty, 0);
  const chain = localLevel ? lineage(localLevel) : undefined;
  const ready =
    lines.length > 0 && name.trim() !== "" && phone !== "" && !!chain && tole.trim() !== "";

  const create = useMutation({
    mutationFn: () => {
      const body: ManualOrderRequest = {
        lines: lines.map((l) => ({ variant_id: l.item.variant_id, qty: l.qty })),
        contact: { name: name.trim(), phone },
        address: {
          province: chain!.province.code,
          district: chain!.district.code,
          local_level: chain!.localLevel.code,
          tole: tole.trim(),
          ...(landmark.trim() ? { landmark: landmark.trim() } : {}),
        },
        service,
        source,
        payment: {
          method,
          status: payStatus,
          paid_paisa: payStatus === "partial" ? (paid ?? 0) : 0,
        },
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      return commerceApi.createManual(orgId, storeId, body, keyFor(body));
    },
    onSuccess: async (r) => {
      toast.show({
        tone: "success",
        title: t("commerce.orders.create.created", { number: r.number }),
      });
      await qc.invalidateQueries({ queryKey: commerceKeys.all(orgId, storeId) });
      onCreated(r.id);
    },
    onError: (e) => toast.show({ tone: "error", title: errorMessage(e) }),
  });

  const setQty = (variantId: string, qty: number) =>
    setLines((ls) =>
      qty <= 0
        ? ls.filter((l) => l.item.variant_id !== variantId)
        : ls.map((l) => (l.item.variant_id === variantId ? { ...l, qty } : l)),
    );

  return (
    <div className="flex flex-col">
      <header className="flex items-center gap-2 border-b border-subtle pb-2.5">
        <span className="flex size-7 items-center justify-center rounded-xs ady-bg-brand text-on-primary">
          <Icon name="cart" />
        </span>
        <h2 className="flex-1 text-base font-semibold">{t("commerce.orders.create.title")}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
        >
          <Icon name="x" />
          <span className="ady-sr-only">{t("commerce.orders.create.cancel")}</span>
        </button>
      </header>

      <div className="grid gap-3 pt-3 lg:grid-cols-[minmax(0,1fr)_17.5rem]">
        <div className="flex min-w-0 flex-col gap-2">
          <Section icon="users" title={t("commerce.orders.create.secCustomer")}>
            <div className="grid gap-1.5 sm:grid-cols-2">
              <PhoneInput
                value={phone}
                aria-label={t("commerce.orders.create.phone")}
                placeholder={t("commerce.orders.create.phoneHint")}
                onValueChange={(v, result) => {
                  setPhone(v);
                  setFound(null);
                  setPhoneIssue(
                    result.ok || result.error === "empty" ? null : (phoneError(result) ?? null),
                  );
                }}
                onBlur={() => setPhoneLeft(true)}
                aria-invalid={phoneLeft && phoneIssue ? true : undefined}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && phone) findCustomer.mutate();
                }}
                suffix={
                  <button
                    type="button"
                    disabled={!phone || findCustomer.isPending}
                    onClick={() => findCustomer.mutate()}
                    className="-me-2 flex items-center gap-1 rounded-xs bg-selected px-2 py-1 text-xs font-semibold text-link disabled:opacity-50"
                  >
                    <Icon name="search" />
                    {t("commerce.orders.create.find")}
                  </button>
                }
              />
              <IconInput
                icon="users"
                label={t("commerce.orders.create.name")}
                value={name}
                onChange={setName}
                maxLength={120}
              />
            </div>
            {phoneLeft && phoneIssue ? (
              <p className="flex items-center gap-1 text-xs text-danger-fg" role="alert">
                <Icon name="triangle" />
                {phoneIssue}
              </p>
            ) : null}
            {typeof found === "number" ? (
              <p className="flex items-center gap-1 text-xs text-success-fg" role="status">
                <Icon name="check" />
                {t("commerce.orders.create.returning", { count: found })}
              </p>
            ) : found === "none" ? (
              <p className="text-xs text-fg-secondary" role="status">
                {t("commerce.orders.create.noCustomer")}
              </p>
            ) : null}
          </Section>

          <Section icon="mapPin" title={t("commerce.orders.create.secAddress")}>
            <CityPicker
              query={cityQuery}
              onQuery={(q) => {
                setCityQuery(q);
                setLocalLevel(null);
              }}
              value={localLevel}
              onPick={(code, label) => {
                setLocalLevel(code);
                setCityQuery(label);
                setDistrict(lineage(code)?.district.code ?? null);
              }}
              district={district}
              onDistrict={(d) => {
                setDistrict(d);
                // A city from another district no longer fits: clear it.
                if (d && localLevel && lineage(localLevel)?.district.code !== d) {
                  setLocalLevel(null);
                  setCityQuery("");
                }
              }}
            />
            <div className="grid gap-1.5 sm:grid-cols-2">
              <IconInput
                icon="home"
                label={t("commerce.orders.create.tolePh")}
                value={tole}
                onChange={setTole}
                maxLength={200}
              />
              <IconInput
                icon="flag"
                label={t("commerce.orders.create.landmarkPh")}
                value={landmark}
                onChange={setLandmark}
                maxLength={200}
              />
            </div>
          </Section>

          <Section
            icon="box"
            title={
              lines.length
                ? t("commerce.orders.create.secProductsCount", { count: lines.length })
                : t("commerce.orders.create.secProducts")
            }
            aside={
              lines.length ? (
                <button
                  type="button"
                  onClick={() => setPicking(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-link"
                >
                  <Icon name="plus" />
                  {t("commerce.orders.create.addProducts")}
                </button>
              ) : null
            }
          >
            {lines.length === 0 ? (
              <button
                type="button"
                onClick={() => setPicking(true)}
                className="flex items-center gap-2 rounded-sm border border-dashed border-control px-2.5 py-1.5 text-start hover:border-link hover:bg-selected"
              >
                <span className="flex size-7 flex-none items-center justify-center rounded-xs bg-selected text-link">
                  <Icon name="plus" />
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">
                    {t("commerce.orders.create.addProducts")}
                  </span>
                  <span className="text-xs text-fg-secondary">
                    {t("commerce.orders.create.addHint")}
                  </span>
                </span>
              </button>
            ) : (
              <ul className="flex flex-col divide-y divide-subtle rounded-sm border border-card">
                {lines.map((l) => (
                  <li
                    key={l.item.variant_id}
                    className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto_4.5rem_1.25rem] items-center gap-2 px-2 py-1"
                  >
                    <span className="flex size-7 items-center justify-center rounded-xs bg-sunken text-fg-tertiary">
                      <Icon name="box" />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold">{l.item.product_title}</span>
                      <span className="truncate text-xs text-fg-tertiary">
                        {[
                          l.item.variant_title,
                          t("commerce.orders.create.each", { price: money(l.item.price_paisa) }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <Stepper
                      qty={l.qty}
                      title={l.item.product_title}
                      max={l.item.available}
                      onChange={(q) => setQty(l.item.variant_id, q)}
                    />
                    <span className="text-end text-sm">
                      <Money paisa={l.item.price_paisa * l.qty} strong />
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty(l.item.variant_id, 0)}
                      title={t("commerce.orders.create.remove", { title: l.item.product_title })}
                      className="flex items-center justify-center text-fg-tertiary hover:text-danger-fg"
                    >
                      <Icon name="trash" />
                      <span className="ady-sr-only">
                        {t("commerce.orders.create.remove", { title: l.item.product_title })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="flex flex-col gap-2.5 rounded-sm border border-card bg-sunken p-3">
          <dl className="flex flex-col gap-1.5 rounded-sm border border-card bg-surface px-3 py-2.5 text-xs">
            <Row
              label={
                count
                  ? t("commerce.orders.create.subtotal", { count })
                  : t("commerce.orders.create.subtotalShort")
              }
            >
              <Money paisa={subtotal} />
            </Row>
            <Row label={t("commerce.orders.create.delivery")}>
              <span className="text-fg-tertiary">{t("commerce.orders.create.fromAddress")}</span>
            </Row>
            <div className="mt-0.5 flex items-center justify-between border-t border-subtle pt-1.5 text-sm font-bold">
              <dt>{t("commerce.orders.create.total")}</dt>
              <dd className="ady-text-brand">
                <Money paisa={subtotal} strong />
              </dd>
            </div>
          </dl>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-secondary">
              {t("commerce.orders.create.source")}
            </span>
            <div
              role="group"
              aria-label={t("commerce.orders.create.source")}
              className="flex flex-wrap gap-1"
            >
              {SOURCES.map((s) => {
                const on = s.id === source;
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setSource(s.id)}
                    className={cx(
                      "flex min-h-7 items-center gap-1 rounded-full border-[1.5px] px-2.5 text-xs font-medium",
                      on
                        ? "ady-outline-brand font-semibold text-link"
                        : "border-card bg-surface text-fg-secondary hover:bg-hover",
                    )}
                  >
                    {s.brand ? (
                      <BrandIcon brand={s.brand} size={14} />
                    ) : s.icon ? (
                      <Icon name={s.icon} />
                    ) : null}
                    {t(`commerce.orders.create.sources.${s.id}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <IconSelect
              icon="truck"
              label={t("commerce.orders.create.service")}
              value={service}
              onChange={(e) => setService(e.target.value as Service)}
              options={(["standard", "express", "pickup"] as const).map((s) => ({
                value: s,
                label: t(`commerce.orders.create.services.${s}`),
              }))}
            />
            <IconSelect
              icon="card"
              mark={<MethodMark method={method} />}
              label={t("commerce.orders.create.payment")}
              value={method}
              onChange={(e) => setMethod(e.target.value as ManualPaymentMethod)}
              options={METHODS.map((m) => ({
                value: m,
                label: t(`commerce.orders.create.methods.${m}`),
              }))}
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-secondary">
              {t("commerce.orders.create.paymentStatus")}
            </span>
            <div
              role="group"
              aria-label={t("commerce.orders.create.paymentStatus")}
              className="grid grid-cols-3 gap-0.5 rounded-xs border border-control bg-surface p-0.5"
            >
              {(["unpaid", "paid", "partial"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={payStatus === s}
                  onClick={() => setPayStatus(s)}
                  className={cx(
                    "min-h-7 rounded-xs text-xs font-medium",
                    payStatus === s
                      ? "ady-bg-brand text-on-primary"
                      : "text-fg-secondary hover:bg-hover",
                  )}
                >
                  {t(`commerce.orders.create.paymentStatuses.${s}`)}
                </button>
              ))}
            </div>
          </div>
          {payStatus === "partial" ? (
            <MoneyInput
              value={paid}
              onValueChange={(v) => setPaid(v)}
              aria-label={t("commerce.orders.create.paidAmount")}
              suffix={
                <span className="text-xs text-fg-tertiary">
                  {t("commerce.orders.create.paidNow")}
                </span>
              }
            />
          ) : null}
          <IconInput
            icon="file"
            label={t("commerce.orders.create.notePh")}
            value={note}
            onChange={setNote}
            maxLength={500}
          />

          <div className="mt-auto grid grid-cols-[1fr_2fr] gap-2 pt-1">
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("commerce.orders.create.cancel")}
            </Button>
            <Button
              icon="check"
              fullWidth
              // Not `availability`: its tooltip would stick out past the dialog's right edge.
              disabled={!ready}
              title={ready ? undefined : t("commerce.orders.create.needs")}
              className="disabled:cursor-not-allowed disabled:opacity-45"
              loading={create.isPending}
              loadingLabel={t("commerce.orders.create.creating")}
              onClick={() => create.mutate()}
            >
              {t("commerce.orders.create.submit")}
            </Button>
          </div>
        </div>
      </div>

      <ProductPicker
        open={picking}
        orgId={orgId}
        storeId={storeId}
        initial={lines}
        onClose={() => setPicking(false)}
        onConfirm={(next) => {
          setLines(next);
          setPicking(false);
        }}
      />
    </div>
  );
}

export function Section({
  icon,
  title,
  aside,
  children,
}: {
  icon: IconName;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="flex items-center gap-1.5 text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
        <span className="text-link">
          <Icon name={icon} />
        </span>
        <span className="flex-1">{title}</span>
        {aside}
      </h3>
      {children}
    </section>
  );
}

const frame =
  "relative flex min-h-control items-center rounded-xs border border-control bg-surface text-sm focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-focus";

/** A text field whose label is its placeholder, with a leading icon (also used by Add customer). */
export function IconInput({
  icon,
  label,
  value,
  onChange,
  maxLength,
  type,
  disabled,
}: {
  icon: IconName;
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  type?: "text" | "email";
  disabled?: boolean;
}) {
  return (
    <label className={cx(frame, disabled && "bg-sunken text-fg-tertiary")}>
      <span className="ps-2.5 text-fg-tertiary">
        <Icon name={icon} />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        aria-label={label}
        maxLength={maxLength}
        type={type}
        disabled={disabled}
        className="min-w-0 flex-1 bg-transparent px-2 outline-none placeholder:text-fg-tertiary disabled:cursor-not-allowed"
      />
    </label>
  );
}

export function IconSelect({
  icon,
  mark,
  label,
  options,
  ...rest
}: {
  icon: IconName;
  /** Shown instead of the icon, e.g. the chosen payment method's logo. */
  mark?: ReactNode;
  label: string;
  options: { value: string; label: string; disabled?: boolean }[];
} & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      <span className={frame}>
        <span className="pointer-events-none absolute start-2.5 flex text-fg-tertiary">
          {mark ?? <Icon name={icon} />}
        </span>
        <select
          {...rest}
          className="min-h-control w-full min-w-0 appearance-none truncate bg-transparent ps-8 pe-7 outline-none"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute end-2 text-fg-tertiary">
          <Icon name="chevronDown" />
        </span>
      </span>
    </label>
  );
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 text-fg-secondary">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Stepper({
  qty,
  title,
  max,
  onChange,
}: {
  qty: number;
  title: string;
  max: number | null;
  onChange: (q: number) => void;
}) {
  const btn =
    "flex size-6 items-center justify-center text-fg-secondary hover:text-link disabled:opacity-40";
  return (
    <span className="inline-flex items-center rounded-xs border border-control">
      <button
        type="button"
        className={btn}
        onClick={() => onChange(qty - 1)}
        title={t("commerce.orders.create.decrease", { title })}
      >
        <Icon name="minus" />
        <span className="ady-sr-only">{t("commerce.orders.create.decrease", { title })}</span>
      </button>
      <span className="ady-ledger min-w-5 text-center text-sm font-semibold">{qty}</span>
      <button
        type="button"
        className={btn}
        disabled={max !== null && qty >= max}
        onClick={() => onChange(qty + 1)}
        title={t("commerce.orders.create.increase", { title })}
      >
        <Icon name="plus" />
        <span className="ady-sr-only">{t("commerce.orders.create.increase", { title })}</span>
      </button>
    </span>
  );
}

/** Nepal's 77 districts, A–Z, for the District dropdown. */
const DISTRICT_OPTIONS = [...DISTRICTS].sort((a, b) => a.nameEn.localeCompare(b.nameEn));

/**
 * City / municipality search over the Nepal address list, next to a District dropdown (owner,
 * 2026-10-21, in place of the ward). A chosen district narrows the search (and lists its cities
 * before typing); picking a city sets its district. The box fits the longest name, Nawalparasi East.
 */
function CityPicker({
  query,
  onQuery,
  value,
  onPick,
  district,
  onDistrict,
}: {
  query: string;
  onQuery: (q: string) => void;
  value: string | null;
  onPick: (code: string, label: string) => void;
  district: string | null;
  onDistrict: (d: string | null) => void;
}) {
  const [focused, setFocused] = useState(false);
  const hits = useMemo(() => {
    if (value) return [];
    if (!query.trim()) return district && focused ? localLevelsOf(district) : [];
    return searchLocalLevels(query, 60)
      .filter((l) => !district || l.districtCode === district)
      .slice(0, 8);
  }, [query, value, district, focused]);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_11.5rem] gap-1.5">
      <div
        className="relative"
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
        }}
      >
        <IconInput
          icon="mapPin"
          label={t("commerce.orders.create.cityPh")}
          value={query}
          onChange={onQuery}
        />
        {hits.length ? (
          <ul
            role="listbox"
            className="absolute inset-x-0 top-full z-10 mt-1 max-h-60 overflow-y-auto rounded-sm border border-card bg-surface py-1 shadow-md"
          >
            {hits.map((l) => {
              const label = `${l.nameEn}, ${getDistrict(l.districtCode)?.nameEn ?? ""}`;
              return (
                <li key={l.code} role="option" aria-selected={false}>
                  <button
                    type="button"
                    onClick={() => onPick(l.code, label)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm hover:bg-hover"
                  >
                    <span className="text-fg-tertiary">
                      <Icon name="mapPin" />
                    </span>
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <span className={frame}>
        <span className="pointer-events-none absolute start-2.5 text-fg-tertiary">
          <Icon name="map" />
        </span>
        <select
          aria-label={t("commerce.orders.create.district")}
          value={district ?? ""}
          onChange={(e) => onDistrict(e.target.value || null)}
          className="min-h-control w-full min-w-0 appearance-none bg-transparent ps-8 pe-7 outline-none"
        >
          <option value="">{t("commerce.orders.create.districtPh")}</option>
          {DISTRICT_OPTIONS.map((d) => (
            <option key={d.code} value={d.code}>
              {d.nameEn}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute end-2 text-fg-tertiary">
          <Icon name="chevronDown" />
        </span>
      </span>
    </div>
  );
}

/** "Select products": one row per variant with stock and price, ticked rows join the order. Marketing
 * (flash deals, checkout links, affiliates) reuses it with its own button label; a deal may also pick
 * items that are out of stock. */
export function ProductPicker({
  open,
  orgId,
  storeId,
  initial,
  onClose,
  onConfirm,
  confirmLabel,
  allowOutOfStock,
}: {
  open: boolean;
  orgId: string;
  storeId: string;
  initial: Line[];
  onClose: () => void;
  onConfirm: (lines: Line[]) => void;
  confirmLabel?: (count: number) => string;
  allowOutOfStock?: boolean;
}) {
  return (
    <Popup open={open} onClose={onClose} size="md" label={t("commerce.orders.create.pickTitle")}>
      {open ? (
        <PickerBody
          orgId={orgId}
          storeId={storeId}
          initial={initial}
          onClose={onClose}
          onConfirm={onConfirm}
          confirmLabel={confirmLabel}
          allowOutOfStock={allowOutOfStock}
        />
      ) : null}
    </Popup>
  );
}

function PickerBody({
  orgId,
  storeId,
  initial,
  onClose,
  onConfirm,
  confirmLabel,
  allowOutOfStock,
}: {
  orgId: string;
  storeId: string;
  initial: Line[];
  onClose: () => void;
  onConfirm: (lines: Line[]) => void;
  confirmLabel?: (count: number) => string;
  allowOutOfStock?: boolean;
}) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(() => new Map(initial.map((l) => [l.item.variant_id, l])));
  // One lookup once typing pauses, not one per letter.
  const term = useDebounced(q.trim());
  const items = useQuery({
    queryKey: ["pos", orgId, storeId, "lookup", term],
    queryFn: ({ signal }) => posApi.lookup(orgId, storeId, { q: term }, signal),
    placeholderData: (prev) => prev,
  });
  const toggle = (item: PosItem) =>
    setPicked((m) => {
      const next = new Map(m);
      if (next.has(item.variant_id)) next.delete(item.variant_id);
      else next.set(item.variant_id, { item, qty: 1 });
      return next;
    });
  const rows = items.data?.data ?? [];
  return (
    <div className="flex flex-col gap-2">
      <header className="flex items-center gap-2 border-b border-subtle pb-2.5">
        <span className="flex size-7 items-center justify-center rounded-xs ady-bg-brand text-on-primary">
          <Icon name="box" />
        </span>
        <h2 className="flex-1 text-base font-semibold">{t("commerce.orders.create.pickTitle")}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
        >
          <Icon name="x" />
          <span className="ady-sr-only">{t("commerce.orders.create.cancel")}</span>
        </button>
      </header>
      <IconInput
        icon="search"
        label={t("commerce.orders.create.pickSearch")}
        value={q}
        onChange={setQ}
      />
      <ul className="flex flex-col divide-y divide-subtle rounded-sm border border-card bg-surface">
        {rows.map((r) => {
          const on = picked.has(r.variant_id);
          const out = r.available === 0 && !on && !allowOutOfStock;
          return (
            <li key={r.variant_id}>
              <label
                className={cx(
                  "grid grid-cols-[1rem_1.75rem_minmax(0,1fr)_auto_4rem] items-center gap-2 px-2 py-1",
                  out ? "opacity-50" : "cursor-pointer",
                  on ? "bg-selected" : !out && "hover:bg-hover",
                )}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={out}
                  onChange={() => toggle(r)}
                  aria-label={t("commerce.orders.create.pickRow", { title: r.product_title })}
                  className="size-4 accent-(--ady-color-action-primary-bg)"
                />
                <span className="flex size-7 items-center justify-center rounded-xs bg-sunken text-fg-tertiary">
                  <Icon name="box" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-semibold">{r.product_title}</span>
                  <span className="truncate text-xs text-fg-tertiary">
                    {[r.variant_title, r.sku].filter(Boolean).join(" · ") || "—"}
                  </span>
                </span>
                <span
                  className={cx(
                    "ady-ledger rounded-full px-2 py-0.5 text-2xs font-medium",
                    r.available === 0
                      ? "bg-danger-bg text-danger-fg"
                      : r.available !== null && r.available <= 3
                        ? "bg-warning-bg text-warning-fg"
                        : "bg-sunken text-fg-secondary",
                  )}
                >
                  {r.available === null
                    ? t("commerce.orders.create.untracked")
                    : r.available === 0
                      ? t("commerce.orders.create.outOfStock")
                      : t("commerce.orders.create.inStock", { count: r.available })}
                </span>
                <span className="text-end text-sm">
                  <Money paisa={r.price_paisa} strong />
                </span>
              </label>
            </li>
          );
        })}
        {items.isSuccess && rows.length === 0 ? (
          <li className="p-4 text-center text-sm text-fg-secondary">
            {t("commerce.orders.create.pickEmpty")}
          </li>
        ) : null}
        {items.isError ? (
          <li className="p-4 text-center text-sm text-danger-fg">{errorMessage(items.error)}</li>
        ) : null}
      </ul>
      <div className="sticky -bottom-3 -mx-3 flex items-center justify-between gap-2 border-t border-subtle bg-sunken px-3 py-2.5 sm:-bottom-4 sm:-mx-4 sm:px-4">
        <span className="text-xs text-fg-secondary">
          {t("commerce.orders.create.pickedCount", { count: picked.size })}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("commerce.orders.create.cancel")}
          </Button>
          <Button
            icon="check"
            disabled={picked.size === 0}
            className="disabled:cursor-not-allowed disabled:opacity-45"
            onClick={() => onConfirm([...picked.values()])}
          >
            {confirmLabel
              ? confirmLabel(picked.size)
              : t("commerce.orders.create.pickConfirm", { count: picked.size })}
          </Button>
        </div>
      </div>
    </div>
  );
}
